import { CheckCircle2, Circle, Rocket } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useParent } from './ParentContext';

interface Setup {
  show: boolean;
  done: boolean;
  steps: { id: 'child' | 'pair' | 'hours' | 'assignment'; done: boolean; childId: string; childName: string }[];
}

const TEXT: Record<Setup['steps'][number]['id'], { title: string; hint: (name: string) => string; action: string }> = {
  child: { title: 'Add your child', hint: () => 'Their name, grade and subjects. Add as many children as you have.', action: 'Add a child' },
  pair: {
    title: 'Set up their computer',
    hint: (name) => `Install ClassGuardian on ${name || 'their'}${name ? '’s' : ''} computer and pair it with a code. About 5 minutes.`,
    action: 'Pair a computer',
  },
  hours: { title: 'Set school hours', hint: (name) => `When school time starts and ends${name ? ` for ${name}` : ''}, day by day.`, action: 'Set hours' },
  assignment: { title: 'Add a first assignment', hint: (name) => `Put something on ${name ? `${name}’s` : 'the'} planner so they see what’s next.`, action: 'Add an assignment' },
};

/**
 * The first-run checklist on Home (#552): add a child, pair their computer, set school hours, add a first assignment.
 * Each step ticks itself off from what's really set up; it goes away when all are done or the parent hides it. The
 * admin can turn it off or choose the steps (Server settings → Accounts).
 */
export function SetupChecklist({ onAddChild, onShown }: { onAddChild: () => void; onShown?: (shown: boolean) => void }) {
  const { children, toast } = useParent();
  const [setup, setSetup] = useState<Setup | null>(null);
  const load = useCallback(() => {
    api
      .get<Setup>('/api/setup')
      .then((s) => {
        setSetup(s);
        onShown?.(s.show);
      })
      .catch(() => onShown?.(false));
  }, [onShown]);
  // Children, their devices and schedules change as the parent works through it: check again when they do.
  const signature = children.map((c) => `${c.id}:${c.devices?.length ?? 0}`).join(',');
  useEffect(load, [load, signature]);
  useEffect(() => {
    const again = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', again);
    return () => document.removeEventListener('visibilitychange', again);
  }, [load]);

  if (!setup?.show) return null;
  const doneCount = setup.steps.filter((s) => s.done).length;
  const next = setup.steps.find((s) => !s.done);
  async function hide() {
    try {
      await api.post('/api/setup/dismiss');
      setSetup((s) => (s ? { ...s, show: false } : s));
      onShown?.(false);
      toast({ text: 'Hidden. Everything is still in each child’s page and in Help.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  return (
    <section className="card stack setup-checklist" aria-label="Get started">
      <div className="setup-head">
        <h2 className="with-icon">
          <Rocket size={20} /> Get started
        </h2>
        <span className="muted small">
          {doneCount} of {setup.steps.length} done
        </span>
      </div>
      <div className="setup-bar" role="progressbar" aria-valuemin={0} aria-valuemax={setup.steps.length} aria-valuenow={doneCount}>
        <span style={{ width: `${(doneCount / setup.steps.length) * 100}%` }} />
      </div>
      <ol className="setup-steps">
        {setup.steps.map((s) => {
          const t = TEXT[s.id];
          const isNext = s === next;
          return (
            <li key={s.id} className={`setup-step ${s.done ? 'done' : ''} ${isNext ? 'next' : ''}`}>
              {s.done ? <CheckCircle2 size={22} className="setup-icon" aria-label="Done" /> : <Circle size={22} className="setup-icon" aria-label="To do" />}
              <div className="grow">
                <strong>{t.title}</strong>
                {!s.done && <div className="muted small">{t.hint(s.childName)}</div>}
              </div>
              {!s.done &&
                (s.id === 'child' ? (
                  <button className={`btn small ${isNext ? 'primary' : ''}`} onClick={onAddChild}>
                    {t.action}
                  </button>
                ) : s.childId ? (
                  <Link className={`btn small ${isNext ? 'primary' : ''}`} to={`/kids/${s.childId}/${s.id === 'pair' ? 'devices' : s.id === 'hours' ? 'schedule' : 'planner'}`}>
                    {t.action}
                  </Link>
                ) : null)}
            </li>
          );
        })}
      </ol>
      <div className="row-gap wrap setup-foot">
        <Link to="/help" className="muted small">
          Need a hand? See Help, or call us.
        </Link>
        <button className="link-btn small" onClick={hide}>
          I’ll finish this later
        </button>
      </div>
    </section>
  );
}
